import { useEffect } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// internal import
import Loader from "../../../components/common/Loader";
import { editResellerCustomer } from "../../../features/resellerCustomerAdminApi";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import useDataInputOption from "../../../hooks/useDataInputOption";
import { Form, Formik } from "formik";
import { FtextField } from "../../../components/common/FtextField";
import InformationTooltip from "../../../components/common/tooltipInformation/InformationTooltip";
import useISPowner from "../../../hooks/useISPOwner";
import { informationEnBn } from "../../../components/common/tooltipInformation/informationEnBn";

const ResellerCustomerEdit = ({ show, setShow, customerId, allCustomer }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> Get user & current user data form useISPOwner hooks
  const { bpSettings } = useISPowner();

  // initial loading state
  const [isLoading, setIsLoading] = useState(false);

  // get reseller customer
  const resellerCustomers = useSelector(
    (state) => state?.resellerCustomer?.resellerCustomer,
  );

  // get all reseller customer
  const resellerAllCustomer = useSelector(
    (state) => state?.resellerCustomer?.allResellerCustomer,
  );

  // get all data from redux state
  let resellerCustomer;
  if (allCustomer) {
    resellerCustomer = resellerAllCustomer;
  }
  if (!allCustomer) {
    resellerCustomer = resellerCustomers;
  }

  // get single customer
  const data = resellerCustomer.find((item) => item.id === customerId);

  //---> Local States
  const [autoDisable, setAutoDisable] = useState(true);
  const [nextMonthAutoDisable, setNextMonthAutoDisable] = useState(false);

  useEffect(() => {
    // set customer auto disable
    setAutoDisable(data?.autoDisable);

    // set customer next month auto disable
    setNextMonthAutoDisable(data?.nextMonthAutoDisable);
  }, [data]);

  //---> Call the data input option function
  const inputPermission = {
    customerId: true,
    mikrotik: true,
    mikrotikPackage: true,
    monthlyFee: true,
    billingCycle: true,
    promiseDate: true,
    status: true,
  };

  //---> Get data input option from useDataInputOption hook
  const dataInputOption = useDataInputOption(
    inputPermission,
    data?.userType,
    "edit",
    data,
  );

  // handle submit
  const customerHandler = (formValues) => {
    const { billingCycle, promiseDate, profile, ...rest } = formValues;

    const sendingData = {
      autoDisable: autoDisable,
      nextMonthAutoDisable: nextMonthAutoDisable,
      billingCycle: new Date(billingCycle).toISOString(),
      promiseDate:
        new Date(data.billingCycle).getTime() !==
        new Date(billingCycle).getTime()
          ? new Date(billingCycle)
          : new Date(promiseDate).toISOString(),
      ...rest,
      pppoe: {
        name: data?.pppoe?.name,
        password: data?.pppoe?.password,
        service: data?.pppoe?.service,
        comment: data?.pppoe?.comment,
        profile: profile,
        disabled: data?.pppoe?.disabled,
      },
    };

    let resellerId;
    if (allCustomer) {
      resellerId = data.reseller.id;
    } else {
      resellerId = data.reseller;
    }

    editResellerCustomer(
      dispatch,
      sendingData,
      resellerId,
      customerId,
      setIsLoading,
      allCustomer,
      setShow,
    );
  };

  return (
    <>
      <ComponentCustomModal
        show={show}
        setShow={setShow}
        centered={false}
        size={"md"}
        header={data?.name + " " + t("editProfile")}
        footer={
          <div className="displayGrid1 float-end">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={isLoading}
              onClick={() => setShow(false)}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              form="customerEdit"
              className="btn btn-success"
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : t("save")}
            </button>
          </div>
        }
      >
        <Formik
          initialValues={{
            ...dataInputOption.inputInitialValues,
          }}
          validationSchema={dataInputOption.validationSchema}
          onSubmit={(values) => {
            customerHandler(values);
          }}
          enableReinitialize
        >
          {() => (
            <Form id="customerEdit">
              <div className="displayGrid">
                {dataInputOption.inputOption?.map(
                  (item) => item.isVisible && <FtextField {...item} />,
                )}

                {bpSettings?.hasMikrotik && (
                  <div>
                    <label className="changeLabelFontColor">
                      {t("automaticConnectionOff")}
                    </label>

                    <div className="displayGrid2">
                      <div className="customerAutoDisable">
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          name="autoDisable"
                          id="autoDisable"
                          checked={autoDisable}
                          disabled={nextMonthAutoDisable}
                          onChange={(e) => setAutoDisable(e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="autoDisable"
                        >
                          {t("willContinue")}
                        </label>
                      </div>

                      {data.balance <= 0 && (
                        <div className="d-flex align-items-center">
                          <div className="customerAutoDisable">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              name="nextMonthAutoDisable"
                              id="nextMonthAutoDisable"
                              checked={nextMonthAutoDisable}
                              disabled={autoDisable}
                              onChange={(e) =>
                                setNextMonthAutoDisable(e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="nextMonthAutoDisable"
                            >
                              {t("nextMonth")}
                            </label>
                          </div>

                          {/* there is information to grant permission tooltip */}
                          {informationEnBn()?.[1] && (
                            <InformationTooltip data={informationEnBn()?.[1]} />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </ComponentCustomModal>
    </>
  );
};

export default ResellerCustomerEdit;
